import { useEffect, useRef, useState } from "react";
import type { ID } from "types";
import { useTypedSelector } from "hooks";
import { FormAdapter } from "hooks/useForm";
import { SetupIdentifier } from "consts/setups";
import { getOptions } from "util/option";
import useMerchantActions from "features/merchants/useMerchantActions";
import { selectMerchantOptions } from "features/merchants/selectors";
import { selectIsConstantsLoading } from "features/general/selectors";
import { selectDomainOptions, selectIsSetupsLoading } from "features/setups/selectors";
import type { UseUserArg, User, UserResponse } from "./types";
import useActions from "./useActions";
import {
    selectIsRolesLoading,
    selectIsUsersMerchantsLoading,
    selectIsUsersMorsLoading,
    selectMerchantsUserBelongs,
    selectMorsUserBelongs,
    selectRoleOptions
} from "./selectors";
import useUserAssociations from "./useUserAssociations";
import useUserRoles from "./useUserRoles";

export const useUpdateOrCreateUser = ({ user, onUserUpdateOrCreate }: UseUserArg) => {
    const [isCreateOrUpdaterequestLoading, setUpdateOrCreateRequestState] = useState(false);

    const merchantActions = useMerchantActions();
    const merchantActionsRef = useRef(merchantActions);
    merchantActionsRef.current = merchantActions;

    const userActions = useActions();
    const userActionsRef = useRef(userActions);
    userActionsRef.current = userActions;

    const { updateOrCreateUser } = userActions;

    const isConstantsLoading = useTypedSelector(selectIsConstantsLoading);
    const isSetupsLoading = useTypedSelector(selectIsSetupsLoading(SetupIdentifier.Mors));
    const isRolesLoading = useTypedSelector(selectIsRolesLoading);
    const isUsersMerchantsLoading = useTypedSelector(selectIsUsersMerchantsLoading);
    const isUsersMorsLoading = useTypedSelector(selectIsUsersMorsLoading);

    const merchantIds: Array<ID> = useTypedSelector(selectMerchantsUserBelongs(user.coreId));
    const mors: Array<string> = useTypedSelector(selectMorsUserBelongs(user.coreId));

    const bootstrapData = {
        domains: useTypedSelector(selectDomainOptions),
        merchants: useTypedSelector(selectMerchantOptions),
        roles: useTypedSelector(selectRoleOptions)
    };

    const {
        updateUserMerchantAssosiation,
        updateUserMorAssociation,
    } = useUserAssociations(user);

    useUserRoles();

    const onSaveOrCreate = async ({
        role,
        merchants = [],
        domains = [],
        ...userRequest
    }: Partial<User>) => {
        setUpdateOrCreateRequestState(true);

        try {
            const response = await updateOrCreateUser(userRequest);

            if (!response) {
                return;
            }

            const { data: user } = response;

            await Promise.allSettled([
                updateUserMerchantAssosiation({
                    user,
                    associations: merchants
                }),
                updateUserMorAssociation({
                    user,
                    associations: domains
                })
            ]);

            const [request] = userActionsRef.current.getUserById(user.coreId);
            onUserUpdateOrCreate?.(await request as UserResponse);

            return FormAdapter.fromResponse(response);

        } finally {
            setUpdateOrCreateRequestState(false);
        }
    };

    const coreId = user.coreId;

    useEffect(() => {
        merchantActionsRef.current.getAllMerchants();

        const {
            getUsersMerchants,
            getUsersMors
        } = userActionsRef.current;;

        if (coreId) {
            getUsersMerchants(coreId);
            getUsersMors(coreId);
        }
    }, [coreId]);

    const initialValues = {
        ...user,
        merchants: getOptions(bootstrapData.merchants, merchantIds),
        domains: getOptions(bootstrapData.domains, mors)
    };

    return {
        initialValues,
        bootstrapData,
        isBootstrapDataLoading: (
            isRolesLoading ||
            isConstantsLoading ||
            isSetupsLoading ||
            isUsersMerchantsLoading ||
            isUsersMorsLoading
        ),
        isCreateOrUpdaterequestLoading,
        isRolesLoading,
        isUsersMerchantsLoading,
        isUsersMorsLoading,
        merchantIds,
        mors,
        onSaveOrCreate
    };
};

export default useUpdateOrCreateUser;
